import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { performLegalWebSearch, formatSearchResultsForAI } from "@/services/legalSearchService";
import DOMPurify from "dompurify";

interface CountryLaw {
  name: string;
  url: string;
}

const countryLaws: CountryLaw[] = [
  { name: "Australia", url: "https://www.legislation.gov.au/" },
  { name: "Canada", url: "https://laws-lois.justice.gc.ca/" },
  { name: "France", url: "https://www.legifrance.gouv.fr/" },
  { name: "Germany", url: "https://www.gesetze-im-internet.de/" },
  { name: "India", url: "https://www.indiacode.nic.in/" },
  { name: "Ireland", url: "http://www.irishstatutebook.ie/" },
  { name: "Italy", url: "https://www.normattiva.it/" },
  { name: "Japan", url: "http://www.japaneselawtranslation.go.jp/" },
  { name: "Mexico", url: "https://www.dof.gob.mx/" },
  { name: "New Zealand", url: "https://www.legislation.govt.nz/" },
  { name: "Singapore", url: "https://sso.agc.gov.sg/" },
  { name: "South Africa", url: "https://www.acts.co.za/" },
  { name: "United Kingdom", url: "https://www.legislation.gov.uk/" },
  { name: "United States", url: "https://uscode.house.gov/" }
];

export const CompareCountriesTab = () => {
  const [country1, setCountry1] = useState<string>("");
  const [country2, setCountry2] = useState<string>("");
  const [question, setQuestion] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  

  const handleSendQuestion = async () => {
    if (!country1 || !country2) {
      toast.error("Please select both countries first");
      return;
    }
    if (country1 === country2) {
      toast.error("Please select two different countries");
      return;
    }
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    setIsLoading(true);
    setResponse("");

    const selectedCountry1 = countryLaws.find(country => country.name === country1);
    const selectedCountry2 = countryLaws.find(country => country.name === country2);
    
    try {
      // Perform web searches for both countries
      console.log('Performing web search for comparative law:', question, country1, country2);
      
      const [webSearchResult1, webSearchResult2] = await Promise.all([
        performLegalWebSearch(question, country1, 'Local Laws'),
        performLegalWebSearch(question, country2, 'Local Laws')
      ]);
      
      let searchContext = '';
      let totalResults = 0;
      
      if (webSearchResult1.searchResults && webSearchResult1.searchResults.length > 0) {
        searchContext += `Recent search results for ${country1} laws:
${formatSearchResultsForAI(webSearchResult1.searchResults)}

`;
        totalResults += webSearchResult1.searchResults.length;
      }
      
      if (webSearchResult2.searchResults && webSearchResult2.searchResults.length > 0) {
        searchContext += `Recent search results for ${country2} laws:
${formatSearchResultsForAI(webSearchResult2.searchResults)}

`;
        totalResults += webSearchResult2.searchResults.length;
      }
      
      if (totalResults > 0) {
        console.log('Web search found', totalResults, 'comparative law results');
      } else {
        console.log('No web search results found for comparative law, using AI knowledge base only');
      }

      const systemPrompt = `You are a legal expert assistant specialized in comparative law. Compare and contrast the laws between ${country1} and ${country2} using ${searchContext ? 'the provided recent search results from official legal resources as your primary reference, supplemented by your knowledge of their official legal resources' : 'your knowledge of their official legal resources'} (${selectedCountry1?.url} and ${selectedCountry2?.url}). Provide a clear comparison highlighting key similarities and differences. If recent search results are provided, prioritize that information and cite the sources.`;

      const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-chat-completion', {
        body: {
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: `${searchContext}Question: ${question}` }
          ],
          model: 'gpt-4o-mini'
        }
      });

      if (aiError) {
        throw new Error(`AI API error: ${aiError.message}`);
      }

      const aiResponse = aiData?.choices?.[0]?.message?.content;
      if (!aiResponse) {
        throw new Error('No response received from AI');
      }

      setResponse(aiResponse);
      toast.success("Response received with real-time comparative legal data!");
    } catch (error: any) {
      toast.error(error.message || "Failed to get AI response");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuestion();
    }
  };

  const formatResponse = (text: string) => {
    if (!text) return "";

    let normalizedText = text.replace(/\n{2,}/g, '\n');
    const lines = normalizedText.split('\n');
    const formattedLines = lines.map(line => {
      if (line.startsWith('### ')) {
        return `<div class="text-yellow-400 font-bold">${line.substring(4)}</div>`;
      }
      let formattedLine = line;
      while (formattedLine.includes('**')) {
        const startIdx = formattedLine.indexOf('**');
        const endIdx = formattedLine.indexOf('**', startIdx + 2);
        if (endIdx === -1) break;
        const beforeText = formattedLine.substring(0, startIdx);
        const boldText = formattedLine.substring(startIdx + 2, endIdx);
        const afterText = formattedLine.substring(endIdx + 2);
        formattedLine = `${beforeText}<span class="text-blue-400 font-bold">${boldText}</span>${afterText}`;
      }
      return `<div>${formattedLine}</div>`;
    });
    return formattedLines.join('\n');
  };

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 space-y-6 p-6 h-[580px]">
      <div className="flex gap-8 mb-6 items-start">
        <div className="flex flex-col gap-2">
          <span className="text-white">Select Country 1:</span>
          <div>
            <Select value={country1} onValueChange={setCountry1}>
              <SelectTrigger className="bg-secondary-light/5 border border-primary/20 min-w-[200px] max-w-fit text-white">
                <SelectValue className="!text-white" placeholder="Select first country" />
              </SelectTrigger>
              <SelectContent className="bg-secondary border border-primary/20">
                {countryLaws.map(country => (
                  <SelectItem key={country.name} value={country.name} className="hover:bg-primary/20 text-black">
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-white">Select Country 2:</span>
          <div>
            <Select value={country2} onValueChange={setCountry2}>
              <SelectTrigger className="bg-secondary-light/5 border border-primary/20 min-w-[200px] max-w-fit text-white">
                <SelectValue className="!text-white" placeholder="Select second country" />
              </SelectTrigger>
              <SelectContent className="bg-secondary border border-primary/20">
                {countryLaws.map(country => (
                  <SelectItem key={country.name} value={country.name} className="hover:bg-primary/20 text-black">
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-2 flex-1">
          <span className="text-white">Question:</span>
          <div className="flex gap-4">
            <Textarea 
              value={question} 
              onChange={e => setQuestion(e.target.value)} 
              onKeyPress={handleKeyPress} 
              placeholder="Enter your question to compare laws between the selected countries..." 
              className="flex-1 bg-secondary-light/5 border border-primary/20 rounded-lg bg-background/5 text-white p-2" 
            />
            <Button 
              onClick={handleSendQuestion} 
              disabled={isLoading} 
              className="bg-primary hover:bg-primary/90 self-start text-black rounded-lg"
            >
              Send
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="text-white text-[11px] mb-1">
          (Enhanced with real-time web search from official legal sources. Information provided does not constitute legal advice.)
        </div>
        <ScrollArea className="h-[350px] border border-primary/20 rounded-lg bg-background/5 mb-4 text-white text-[13px] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-primary-light">(Searching for real-time comparative legal data and analyzing...)</p>
            </div>
          ) : (
            <div 
              className="p-4 whitespace-pre-wrap max-h-full overflow-y-auto" 
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatResponse(response), { ALLOWED_TAGS: ['div','span','br','strong','em','b','i','p','ul','ol','li'], ALLOWED_ATTR: ['class'] }) }} 
            />
          )}
        </ScrollArea>
        <p className="text-[12px] text-white">(Note: AI hallucination may occur. Validation of generated information is essential.)</p>
      </div>
    </div>
  );
};
